import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronRight, Download, Search, UserPlus, Users, X } from "lucide-react";
import api from "../../../services/api";
import CreateUserModal from "./CreateUserModal";
import UserDrawer from "./UserDrawer";
import {
  EmptyState,
  Pagination,
  RoleBadge,
  UserStatusBadges,
  blobErrorText,
  cleanParams,
  errorText,
  formatDate,
  fullName,
  initials,
  timeAgo,
  useToast,
} from "./common";

const humanCollection = (name) =>
  String(name)
    .replace(/[_-]+/g, " ")
    .replace(/^\w/, (c) => c.toUpperCase());

const UsersPanel = ({ intent, onDataChanged }) => {
  const { notify } = useToast();
  const [search, setSearch] = useState("");
  const [role, setRole] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ users: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [deletedInfo, setDeletedInfo] = useState(null);
  const requestRef = useRef(0);

  // Quick actions from the Overview tab
  useEffect(() => {
    if (!intent) return;
    if (intent.action === "add-user") setCreateOpen(true);
    if (intent.action === "unverified-users" || intent.action === "suspended-users") {
      setStatus(intent.action === "unverified-users" ? "unverified" : "suspended");
      setRole("");
      setSearch("");
      setPage(1);
    }
  }, [intent]);

  const load = useCallback(async () => {
    const req = ++requestRef.current;
    setLoading(true);
    setError(null);
    try {
      const params = cleanParams({ search: search.trim(), role, status, page, limit: 15 });
      const res = await api.get("/admin/users", { params });
      if (req !== requestRef.current) return;
      setData({
        users: Array.isArray(res.data?.users) ? res.data.users : [],
        total: res.data?.total ?? 0,
        pages: res.data?.pages || 1,
      });
      setLoaded(true);
    } catch (err) {
      if (req !== requestRef.current) return;
      setError(errorText(err, "Couldn't load users."));
    } finally {
      if (req === requestRef.current) setLoading(false);
    }
  }, [page, role, search, status]);

  // Debounce typing in the search box
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = cleanParams({ search: search.trim(), role, status });
      const res = await api.get("/admin/users/export", { params, responseType: "blob" });
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "myedugenius-users.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      notify("Export ready: myedugenius-users.csv");
    } catch (err) {
      notify(await blobErrorText(err, "Couldn't export users."), "error");
    } finally {
      setExporting(false);
    }
  };

  const handleUpdated = (updated) => {
    setData((prev) => ({
      ...prev,
      users: prev.users.map((u) => (u.user_id === updated.user_id ? { ...u, ...updated } : u)),
    }));
    onDataChanged?.();
  };

  const handleDeleted = (user, removed) => {
    setDeletedInfo({ user, removed });
    load();
    onDataChanged?.();
  };

  const filtersActive = Boolean(search.trim() || role || status);
  const clearFilters = () => {
    setSearch("");
    setRole("");
    setStatus("");
    setPage(1);
  };

  const removedEntries = deletedInfo
    ? Object.entries(deletedInfo.removed || {}).filter(([, n]) => Number(n) > 0)
    : [];

  return (
    <div className="dashboard-section admin-panel">
      <div className="admin-toolbar">
        <div className="admin-search">
          <Search size={18} aria-hidden="true" />
          <input
            type="search"
            placeholder="Search by name, email, username or ID"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            aria-label="Search users"
          />
        </div>
        <select
          className="admin-select"
          value={role}
          onChange={(e) => {
            setRole(e.target.value);
            setPage(1);
          }}
          aria-label="Filter by role"
        >
          <option value="">All roles</option>
          <option value="student">Students</option>
          <option value="teacher">Teachers</option>
          <option value="admin">Admins</option>
        </select>
        <select
          className="admin-select"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          aria-label="Filter by status"
        >
          <option value="">Any status</option>
          <option value="active">Active</option>
          <option value="unverified">Unverified</option>
          <option value="suspended">Suspended</option>
        </select>
        <div className="admin-toolbar-actions">
          <button type="button" className="eg-btn eg-btn-outline" onClick={exportCsv} disabled={exporting}>
            <Download size={16} /> {exporting ? "Exporting…" : "Export CSV"}
          </button>
          <button type="button" className="eg-btn eg-btn-accent" onClick={() => setCreateOpen(true)}>
            <UserPlus size={16} /> Add user
          </button>
        </div>
      </div>

      {deletedInfo && (
        <div className="admin-success admin-success-row" role="status">
          <div>
            <strong>{fullName(deletedInfo.user)} ({deletedInfo.user.email}) was deleted.</strong>
            {removedEntries.length > 0 ? (
              <ul className="admin-removed">
                {removedEntries.map(([name, n]) => (
                  <li key={name}>
                    {humanCollection(name)}: <strong>{n}</strong>
                  </li>
                ))}
              </ul>
            ) : (
              <span> No other linked records were found.</span>
            )}
          </div>
          <button type="button" className="admin-icon-btn" onClick={() => setDeletedInfo(null)} aria-label="Dismiss">
            <X size={16} />
          </button>
        </div>
      )}

      {error && (
        <div className="admin-error admin-error-row">
          <span>{error}</span>
          <button type="button" className="eg-btn eg-btn-outline" onClick={load}>
            Try again
          </button>
        </div>
      )}

      <div className={`admin-table-wrap ${loading && loaded ? "is-refreshing" : ""}`} aria-busy={loading}>
        <table className="admin-table">
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Email</th>
              <th scope="col">Role</th>
              <th scope="col">Status</th>
              <th scope="col">Joined</th>
              <th scope="col">Last active</th>
              <th scope="col"><span className="sr-only">Open</span></th>
            </tr>
          </thead>
          <tbody>
            {!loaded && loading ? (
              <tr>
                <td colSpan={7} className="admin-muted">Loading users…</td>
              </tr>
            ) : data.users.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <EmptyState
                    icon={Users}
                    title={filtersActive ? "No users match these filters" : "No users yet"}
                    action={
                      filtersActive && (
                        <button type="button" className="eg-btn eg-btn-outline" onClick={clearFilters}>
                          Clear filters
                        </button>
                      )
                    }
                  />
                </td>
              </tr>
            ) : (
              data.users.map((u) => (
                <tr
                  key={u.user_id}
                  className="admin-row-click"
                  onClick={() => setSelectedId(u.user_id)}
                >
                  <td>
                    <div className="admin-user">
                      <span className="admin-avatar">{initials(u)}</span>
                      <div>
                        <strong>{fullName(u)}</strong>
                        <small>{u.student_id || u.teacher_id || u.username}</small>
                      </div>
                    </div>
                  </td>
                  <td className="admin-email">{u.email}</td>
                  <td><RoleBadge role={u.role} /></td>
                  <td>
                    {u.role === "admin" ? (
                      <span className="admin-badge admin-badge-gold">Admin</span>
                    ) : (
                      <UserStatusBadges user={u} />
                    )}
                  </td>
                  <td className="admin-nowrap">{formatDate(u.created_at)}</td>
                  <td className="admin-nowrap">{u.last_active ? timeAgo(u.last_active) : "—"}</td>
                  <td>
                    <button
                      type="button"
                      className="admin-icon-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedId(u.user_id);
                      }}
                      aria-label={`Open details for ${fullName(u)}`}
                    >
                      <ChevronRight size={18} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} pages={data.pages} total={data.total} noun="user" onPage={setPage} disabled={loading} />

      <CreateUserModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          load();
          onDataChanged?.();
        }}
      />

      <UserDrawer
        userId={selectedId}
        onClose={() => setSelectedId(null)}
        onUpdated={handleUpdated}
        onDeleted={handleDeleted}
      />
    </div>
  );
};

export default UsersPanel;
